// El cuadro que representa a un video — ADR-2026-09-15-un-video-subido-necesita-su-poster.
//
// Por qué hace falta: las miniaturas salen de `opt/<archivo>`, y cuando no hay
// `opt/` se cae a `full/`. Para una foto eso está bien; para un mp4 significa
// pintar un `<img src="...mp4">`, o sea el ícono roto. Un video subido sin
// póster es invisible en toda grilla de la app — ya pasó con las generaciones de
// video sin `opt/*.png`.
//
// Se saca en el NAVEGADOR y en el momento de subir, mientras el archivo está en
// la mano: mandarlo al backend para que lo genere obliga a bajar el video otra
// vez del otro lado.
//
// Alternativa descartada: dibujar un `<video>` en la celda. Bajar ochenta megas
// para pintar un cuadradito de calendario, por cada celda y cada vez.
//
// La técnica está probada en este repo (`projectThumbnail.ts`, la portada del
// editor de video): un `<video>` fuera de pantalla, un seek, y canvas.

/**
 * Los videos que la app maneja. Misma lista que usan las grillas del historial
 * para armar el nombre del póster.
 */
const EXTENSION_DE_VIDEO = /\.(mp4|webm|mov)$/i;

export function esNombreDeVideo(fileName: string): boolean {
  return EXTENSION_DE_VIDEO.test(fileName);
}

/**
 * ¿El archivo elegido es un video? Por el `type` del sistema y, de respaldo, por
 * la extensión: algunos Android entregan el archivo con `type` vacío.
 */
export function esArchivoDeVideo(archivo: File | Blob): boolean {
  if (archivo.type.toLowerCase().startsWith("video/")) return true;
  return "name" in archivo ? esNombreDeVideo((archivo as File).name) : false;
}

/**
 * Dónde vive el póster de un video: al lado, con el mismo nombre y `.png`.
 *
 * Es la convención que ya usa toda la app —el historial, las creaciones
 * recientes, la grilla de resultados— para encontrar la miniatura de un mp4.
 * Está acá para que exista UN lugar donde leerla; los call-sites viejos la
 * repiten inline y conviene irlos trayendo.
 */
export function nombreDelPoster(fileName: string): string {
  return fileName.replace(EXTENSION_DE_VIDEO, ".png");
}

/** Lado más largo del póster. Es el tamaño de `opt/`, que es para grillas. */
const LADO_MAX = 800;

/**
 * PNG y no JPEG, aunque pese más.
 *
 * El nombre del póster termina en `.png` porque ésa es la convención que ya usa
 * toda la app, y es lo que escribe el backend para los videos generados. Meter
 * bytes JPEG adentro de un archivo llamado `.png` anda —el navegador mira el
 * Content-Type— pero deja un archivo que miente sobre lo que es, que es el
 * mismo problema que ya arreglamos en las descargas.
 */
const TIPO_DEL_POSTER = "image/png";

/** Si el video no da un cuadro en este tiempo, se sube sin póster y no se cuelga la subida. */
const ESPERA_MAX_MS = 6000;

/**
 * Un pelín adentro y no en 0: muchos videos abren con un cuadro negro, y un
 * póster negro no distingue un video de otro.
 */
const SEGUNDO_A_TOMAR = 0.5;

/** Cuánto mide el póster manteniendo la proporción. Exportada para poder probarla. */
export function medidasDelPoster(
  ancho: number,
  alto: number,
  ladoMax: number = LADO_MAX,
): { ancho: number; alto: number } {
  if (!ancho || !alto) return { ancho: ladoMax, alto: ladoMax };
  // No se agranda un video chico: quedaría pixelado y pesaría más.
  const escala = Math.min(1, ladoMax / Math.max(ancho, alto));
  return {
    ancho: Math.max(1, Math.round(ancho * escala)),
    alto: Math.max(1, Math.round(alto * escala)),
  };
}

/**
 * Un PNG del video, o `null` si no se pudo.
 *
 * Nunca tira: un póster que falla no puede impedir que el archivo se suba. El
 * que llama decide qué hacer sin él (hoy: subir igual y mostrar un ícono de
 * video en vez de la imagen).
 */
export async function posterDeVideo(archivo: File | Blob): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const url = URL.createObjectURL(archivo);
  try {
    return await new Promise<Blob | null>((resolve) => {
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.preload = "auto";

      let listo = false;
      const terminar = (blob: Blob | null) => {
        if (listo) return;
        listo = true;
        window.clearTimeout(reloj);
        // Soltar el decoder: sin esto queda un video cargado en memoria por cada
        // archivo que se sube.
        video.removeAttribute("src");
        video.load();
        resolve(blob);
      };

      const reloj = window.setTimeout(() => terminar(null), ESPERA_MAX_MS);

      const dibujar = () => {
        try {
          const { ancho, alto } = medidasDelPoster(video.videoWidth, video.videoHeight);
          const canvas = document.createElement("canvas");
          canvas.width = ancho;
          canvas.height = alto;
          const ctx = canvas.getContext("2d");
          if (!ctx) return terminar(null);
          ctx.drawImage(video, 0, 0, ancho, alto);
          canvas.toBlob((b) => terminar(b), TIPO_DEL_POSTER);
        } catch {
          terminar(null);
        }
      };

      video.onloadedmetadata = () => {
        const duracion = video.duration || 0;
        const destino = Math.max(0, Math.min(SEGUNDO_A_TOMAR, duracion - 0.05));
        try {
          video.currentTime = destino;
        } catch {
          // Algunos navegadores no dejan buscar antes de tener datos: se toma
          // lo que haya.
          dibujar();
        }
      };
      video.onseeked = dibujar;
      video.onerror = () => terminar(null);

      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
